import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Button, CircularProgress, IconButton, InputAdornment, LinearProgress, TextField, Tooltip,
        Typography} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlined";
import DownloadIcon from "@mui/icons-material/Download";
import AutoAwesomeOutlined from "@mui/icons-material/AutoAwesomeOutlined";
import FilterListIcon from "@mui/icons-material/FilterList";
import VisibilityOutlined from "@mui/icons-material/VisibilityOutlined";
import HistoryIcon from "@mui/icons-material/History";
// THE FOUNDATION CLIENT, NOT THE BARE ONE. util/Axios sends the access token and nothing else;
// makeCresAxiosFoundationAPI also attaches visibility-mode and visibility-value from the
// active persona. Attachment reads need them: a file has no persona of its own, so the
// server answers "may you see these?" by asking the RECORD, and that read refuses without a
// persona (CC-OBRV-PRSN). On the bare client every attachment listing was one 400 away from
// "These files could not be listed" -- which is what the approval review drawer showed the
// first reviewer to open it.
import {makeCresAxiosFoundationAPI} from "../../util/AxiosFoundationAPI";
import {useSessionContext} from "../../util/SessionContext";
import {fileTypeFor} from "../../util/fileTypeIcon";
import {Blue4, Grey2, Grey6, Grey7, Grey17, Red3} from "../../theme/styles";
import {buildZip, saveBlob} from "../../util/zip";
import AskAboutAttachments from "../Foundation/AskAboutAttachments";
import AttachmentPreviewDialog, {previewKindFor} from "./AttachmentPreviewDialog";
import AttachmentAuditTrailDialog from "./AttachmentAuditTrailDialog";

/**
 * The files attached to a record, or to one File field on its form.
 *
 * THE BYTES DO NOT GO THROUGH THE API, and that is the whole design. API Gateway caps a request at
 * 10MB, so an upload that posted the file to t1 would cap every attachment on the platform at ten
 * megabytes. This asks the server to reserve the file, gets one presigned S3 URL per 5MB part, PUTs
 * the parts straight to S3, and tells the server what S3 said.
 *
 * WHICH IS ALSO WHY THE PART PUT USES `fetch` AND NOT `Axios`. The app's client attaches an
 * Authorization header and a handful of custom ones to every request; a presigned URL is signed over
 * exactly the headers it expects, so an extra one is not ignored -- S3 answers 403 SignatureDoesNotMatch.
 * The PUT below carries the body and nothing else.
 *
 * THIS REPLACES THE OLD AttachmentUpload, which drove the AWS SDK in the browser with credentials
 * from the session. That handed every logged-in tab a live AWS credential whether it ever uploaded or
 * not, and put the bucket name and key layout somewhere a client could argue with them. Here the
 * browser is given URLs and nothing else: it cannot name a bucket, choose a key, or reach another
 * tenant's prefix, and what it holds expires.
 */

type Attachment = {
    USID: string;
    LinkUSID: string;
    Name: string;
    Bytes: number;
    MimeType?: string | null;
    FieldCode?: string | null;
    Origin?: string;
    CreatedDatetime?: string | null;
    /** The row exists and the stored file does not. Said by the server, never inferred here. */
    Missing?: boolean;
    /**
     * Where this file hangs, as four distinct answers rather than "has a field code or not".
     *
     * FROM THE SERVER, because deciding it needs the revision's stored schema and no client has
     * that. A client deriving "a field code is set, so it is a form field" is precisely the
     * derivation that let document figures be counted as the record's attachments.
     */
    Placement?: "Record" | "Field" | "Document" | "Unclaimed";
    /** The field's label off the revision's own schema. Null unless Placement is Field. */
    FieldLabel?: string | null;
    /** The clean copy this rendition carries the approval sheet for (Origin GENERATED only). */
    GeneratedFromUSID?: string | null;
    /** The controlled copy, per file (2026-10-31): "rendition" for the generated copy itself; on a clean PDF
     *  "generated" (its copy exists), "pending" (released, not yet rendered), "off" (the type opts out). */
    ControlledCopy?: "rendition" | "generated" | "pending" | "off" | null;
    ControlledCopyUSID?: string | null;
};

type Props = {
    /** The record these files hang off. */
    sourceUSID: string;
    sourceType?: string;
    /** Set for a File field on the form; omitted for the record's own attachments. */
    fieldCode?: string | null;
    /** Cardinality One: replace rather than accumulate. */
    single?: boolean;
    /** What the one-zip download of the record's own files is called, without the extension; e.g. "SOP-00011-rev1". */
    bundleName?: string;
    /** How the record is named to a reader, e.g. "SOP-00011 Rev 1": the section of its own files is titled with it. */
    recordLabel?: string;
    readOnly?: boolean;
    /** Told after any change, so a form can re-check a required File field. */
    onChange?: () => void;
    /** Record-level panels want a heading; a form field already has a label. */
    title?: string | null;
    /**
     * EVERY FILE ON THE RECORD, grouped by where it hangs -- the record itself, each File field on
     * the form, the document body, and any field that no longer exists.
     *
     * WITHOUT THIS THE PANEL IS A SUBSET WEARING THE NAME OF THE WHOLE. It lists only files with
     * no field code, which in Peak Medical is none of the 494: every one answers a File field, so
     * the record's Attachments tab showed "No files." beside a Has column reading 4. A control
     * claiming "all this record's files" cannot sit above a list that shows some of them.
     *
     * EDITS STAY WHERE THE QUESTION IS. A field's answer is attached and removed on the form,
     * which is where its required-ness is checked; here those files are shown, downloaded and
     * asked about, and only the record's own files can be added or removed. Two write paths to one
     * field's answer is how a required field ends up empty and valid.
     */
    allFiles?: boolean;
};

/** One heading in the all-files view, and whether its files can be changed from here. */
type Group = {
    key: string;
    label: string;
    note?: string;
    files: Attachment[];
    /** Only the record's own group. A field's answer is attached and removed on the form. */
    editable: boolean;
};

/**
 * Where a file hangs, tolerating a server that does not say.
 *
 * THE FALLBACK IS THE OLD TWO-WAY ANSWER, which is the most this client can know on its own: a
 * field code means a field, no field code means the record. It cannot tell a document figure from a
 * retired field's answer -- that needs the revision's schema -- so an older backend degrades to
 * labelling both as fields rather than to inventing a group for them.
 */
function placementOf(attachment: Attachment) {
    return attachment.Placement ?? (attachment.FieldCode ? "Field" : "Record");
}

// Matches PART_SIZE in the service. The server sends its own in the begin response and that is what
// is used -- this is only the fallback for a response that somehow omits it.
const FALLBACK_PART_SIZE = 5 * 1024 * 1024;

export function formatBytes(bytes: number) {
    if (!bytes && bytes !== 0) return "";
    if (bytes < 1024) return `${bytes} B`;
    const units = ["KB", "MB", "GB", "TB"];
    let value = bytes / 1024;
    let unit = 0;
    while (value >= 1024 && unit < units.length - 1) {
        value /= 1024;
        unit += 1;
    }
    return `${value >= 10 ? Math.round(value) : value.toFixed(1)} ${units[unit]}`;
}

export default function Attachments({sourceUSID, sourceType = "ObjectRevision", fieldCode = null,
                                     single = false, readOnly = false, onChange,
                                     title = null, allFiles = false, bundleName, recordLabel}: Props) {
    const sessionContext = useSessionContext();
    // Memoised: it is a dependency of the load callback below, and a fresh client each
    // render would re-run it for ever.
    const Axios = useMemo(() => makeCresAxiosFoundationAPI(sessionContext), [sessionContext]);
    const [attachments, setAttachments] = useState<Attachment[]>([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState<string | null>(null);
    const [progress, setProgress] = useState<number>(0);
    const [error, setError] = useState<string | null>(null);
    // BY NAME ONLY, and said so on the control. Filtering what is already loaded needs no index
    // and no model, so it answers instantly and works on every file in the estate -- including the
    // 487 that have never been indexed, which a content search would silently find nothing in.
    // "Where does this say LOT-2291-B" is the other question, and the ✨ answers it by reading the
    // bytes and citing the page.
    const [filter, setFilter] = useState("");
    const input = useRef<HTMLInputElement | null>(null);
    const [previewing, setPreviewing] = useState<Attachment | null>(null);
    const [auditing, setAuditing] = useState<Attachment | null>(null);

    const load = useCallback(() => {
        if (!sourceUSID) {
            setLoading(false);
            return;
        }
        setLoading(true);
        Axios({method: "GET", url: "t1/v1/attachments",
               params: {SourceUSID: sourceUSID, SourceType: sourceType, FieldCode: fieldCode,
                        // THE WHOLE RECORD OR ONE FIELD, never a muddle of the two. IncludeFields
                        // drops the field-code predicate server-side; FieldCode stays null here so
                        // a stale value could not narrow a listing that claims to be complete.
                        IncludeFields: allFiles ? true : undefined}})
            .then((response: any) => {
                setAttachments(response?.data?.Attachments ?? []);
                setError(null);
            })
            .catch((problem: any) =>
                // The platform puts the human sentence in Cause. Without this the panel could
                // only ever say "could not be listed", which is the shrug that hid a plain
                // persona error for as long as it existed.
                setError(problem?.response?.data?.Cause ?? "These files could not be listed."))
            .finally(() => setLoading(false));
    }, [sourceUSID, sourceType, fieldCode, allFiles, Axios]);

    useEffect(() => { load(); }, [load]);

    const upload = async (file: File) => {
        setBusy("Preparing");
        setProgress(0);
        setError(null);

        let attachmentUSID: string | null = null;
        try {
            const begun = await Axios({
                method: "POST", url: "t1/v1/attachment_uploads",
                data: {SourceUSID: sourceUSID, SourceType: sourceType, FieldCode: fieldCode,
                       Name: file.name, Bytes: file.size,
                       MimeType: file.type || "application/octet-stream"},
            });

            attachmentUSID = begun?.data?.AttachmentUSID;
            const urls: string[] = begun?.data?.PartUrls ?? [];
            const partSize: number = begun?.data?.PartSize ?? FALLBACK_PART_SIZE;

            setBusy("Uploading");

            // SEQUENTIAL, NOT PARALLEL. Parallel parts are faster on a fast link and are how a
            // desktop client would do it, but they also multiply the memory a browser holds and make
            // a partial failure harder to report honestly. One at a time keeps the progress number
            // truthful, which is what somebody watching a large upload actually needs.
            const parts: {PartNumber: number; ETag: string}[] = [];
            for (let index = 0; index < urls.length; index += 1) {
                const chunk = file.slice(index * partSize, (index + 1) * partSize);
                const response = await fetch(urls[index], {method: "PUT", body: chunk});
                if (!response.ok) throw new Error(`Part ${index + 1} was rejected by storage.`);

                // Exposed by the bucket's CORS rule. Without it this reads null and completing the
                // upload is impossible -- S3 requires every part's ETag.
                const etag = response.headers.get("ETag");
                if (!etag) throw new Error("Storage did not return a part identifier.");
                parts.push({PartNumber: index + 1, ETag: etag});
                setProgress(Math.round(((index + 1) / urls.length) * 100));
            }

            setBusy("Finishing");
            await Axios({method: "PUT", url: "t1/v1/attachment_uploads",
                         data: {AttachmentUSID: attachmentUSID, Parts: parts,
                                SourceUSID: sourceUSID, SourceType: sourceType,
                                FieldCode: fieldCode}});

            // Cardinality One: the new file replaces what was there. Done after the new one is
            // stored, so a failed upload leaves the existing file alone.
            if (single) {
                for (const existing of attachments) {
                    await Axios({method: "DELETE", url: "t1/v1/attachments",
                                 params: {AttachmentUSID: existing.USID, SourceUSID: sourceUSID,
                                          SourceType: sourceType, FieldCode: fieldCode}});
                }
            }

            load();
            onChange?.();
        } catch (e: any) {
            setError(e?.response?.data?.Cause ?? e?.message ?? "This file could not be uploaded.");

            // ABANDON THE RESERVATION. Without this the row stays PENDING and the multipart upload
            // bills until the bucket's lifecycle rule expires it seven days later.
            if (attachmentUSID) {
                try {
                    await Axios({method: "DELETE", url: "t1/v1/attachment_uploads",
                                 params: {AttachmentUSID: attachmentUSID}});
                } catch {
                    // The upload already failed; failing to tidy up is not what to report.
                }
            }
        } finally {
            setBusy(null);
            setProgress(0);
            if (input.current) input.current.value = "";
        }
    };

    // THE REVISION BEING LOOKED AT travels with the request (2026-10-31): the same file hangs off every
    // revision that carried it, and which stamp a copy gets -- draft, uncontrolled, obsolete -- is this
    // revision's status, not the newest one's. THE PURPOSE travels too (2026-11-02): each is its own row on
    // the file's audit trail, written by the server before it hands the URL out.
    const urlFor = (attachment: Attachment, purpose: "Download" | "Preview" | "Bundle") =>
        Axios({method: "GET", url: "t1/v1/attachment_downloads",
               params: {AttachmentUSID: attachment.USID, Purpose: purpose,
                        SourceUSID: sourceType === "ObjectRevision" ? sourceUSID : undefined}});

    const download = (attachment: Attachment) => {
        urlFor(attachment, "Download")
            .then((response: any) => {
                const url = response?.data?.Url;
                if (url) window.location.assign(url);
            })
            .catch(() => setError("This file could not be downloaded."));
    };

    const previewUrl = (attachment: Attachment) =>
        urlFor(attachment, "Preview").then((response: any) => {
            const url = response?.data?.Url;
            if (!url) throw new Error("This file could not be previewed.");
            return {url, name: response?.data?.Name, note: response?.data?.Note};
        });

    // EVERY FILE ON THE RECORD ITSELF, AS ONE ZIP (Ed, 2026-10-31: "download all attachments for an
    // object revision, directly associated to the object revision and not from any form field, in one
    // zipped bundle"). The record's own files only -- Placement "Record" -- never a form field's
    // answers, a document's figures or an unclaimed file. Each is fetched by its own presigned URL,
    // exactly as a single download is, and the archive is built here: nothing goes through a function.
    const bundle = () => {
        const own = attachments.filter(one => placementOf(one) === "Record" && !one.Missing);
        if (own.length === 0) return;
        setBusy("Bundling");
        setError(null);
        Promise.all(own.map(attachment =>
            urlFor(attachment, "Bundle")
                .then((response: any) => {
                    const url = response?.data?.Url;
                    if (!url) throw new Error(attachment.Name);
                    return fetch(url).then(reply => {
                        if (!reply.ok) throw new Error(attachment.Name);
                        return reply.arrayBuffer();
                    });
                })
                .then(buffer => ({name: attachment.Name, bytes: new Uint8Array(buffer),
                                  modified: attachment.CreatedDatetime ? new Date(attachment.CreatedDatetime) : undefined}))))
            .then(entries => saveBlob(buildZip(entries), `${bundleName || "attachments"}.zip`))
            .catch((problem: any) => setError(`The files could not be bundled${problem?.message ? ` (${problem.message})` : ""}.`))
            .finally(() => setBusy(null));
    };

    const remove = (attachment: Attachment) => {
        // DETACHES, does not delete. The same file is reachable from every revision that carried it
        // forward, so this must not reach back into the ones before this record.
        Axios({method: "DELETE", url: "t1/v1/attachments",
               params: {AttachmentUSID: attachment.USID, SourceUSID: sourceUSID,
                        SourceType: sourceType, FieldCode: fieldCode}})
            .then(() => { load(); onChange?.(); })
            .catch(() => setError("This file could not be removed."));
    };

    // ASKING IS ONLY OFFERED WHERE IT CAN BE SCOPED. A phase or milestone attachment hangs off
    // something that is not reached through the five visibility modes, so there is no record to
    // prove and the service refuses the scope outright -- better not to offer the button.
    //
    // GATED ON THE FILES THIS SURFACE ACTUALLY HOLDS. It used to read attachments.length on a
    // listing that excluded every form field, so on a record whose files all answer fields -- which
    // in Peak Medical is every record -- the button was hidden although it had 494 files to read.
    // A control's scope and the condition that reveals it have to be the same set.
    const askable = sourceType === "ObjectRevision" && !!sourceUSID && attachments.length > 0;

    // Unfiltered, because this line is the record's shape and must not move as you type.
    const counts = useMemo(() => {
        const of = (placement: string) =>
            attachments.filter(one => placementOf(one) === placement).length;
        return {record: of("Record"), field: of("Field"),
                document: of("Document"), unclaimed: of("Unclaimed")};
    }, [attachments]);

    const needle = filter.trim().toLowerCase();
    const shown = useMemo(
        () => (needle ? attachments.filter(one => one.Name.toLowerCase().includes(needle))
                      : attachments),
        [attachments, needle]);

    // GROUPED BY WHERE EACH FILE HANGS, in the order a reader looks for them: the record's own
    // files, then the form's field by field, then the two kinds that are not the record's evidence
    // at all. A flat list sorted by field code would put a document figure between two
    // certificates and leave the reader to work out why one of them cannot be opened from here.
    const groups = useMemo(() => {
        const of = (placement: string) => shown.filter(one => placementOf(one) === placement);

        const byField = new Map<string, Attachment[]>();
        of("Field").forEach(one => {
            const label = one.FieldLabel || one.FieldCode || "";
            byField.set(label, [...(byField.get(label) ?? []), one]);
        });

        const out: Group[] = [];
        // A RENDITION FOLLOWS ITS CLEAN COPY (2026-10-31): the controlled copy is listed directly under the file
        // it carries the approval sheet for, so the pair reads as one document in two forms.
        const renditions = new Map<string, Attachment[]>();
        of("Record").filter(one => one.ControlledCopy === "rendition" && one.GeneratedFromUSID)
            .forEach(one => renditions.set(one.GeneratedFromUSID!, [...(renditions.get(one.GeneratedFromUSID!) ?? []), one]));
        const record = of("Record").filter(one => !(one.ControlledCopy === "rendition" && one.GeneratedFromUSID && of("Record").some(c => c.USID === one.GeneratedFromUSID)))
            .flatMap(one => [one, ...(renditions.get(one.USID) ?? [])]);
        // SHOWN EVEN WHEN EMPTY, because "Add a file" lives in it and because "none on the record
        // itself" is a fact worth stating on a record whose files are all on the form. Hidden while
        // filtering: an empty group under a search box reads as a result.
        if (record.length || !needle) {
            out.push({key: "record", label: "On the record", editable: true, files: record});
        }
        byField.forEach((files, label) => out.push({
            key: `field:${label}`, label, files, editable: false,
            note: "answers a field on the form — attach and remove it there",
        }));
        const figures = of("Document");
        if (figures.length) {
            out.push({key: "document", label: "In the document body", files: figures,
                      editable: false,
                      note: "a figure inside the document text, read in the document rather than here"});
        }
        const adrift = of("Unclaimed");
        if (adrift.length) {
            out.push({key: "unclaimed", label: "Not on any current field", files: adrift,
                      editable: false,
                      note: "the field that asked for this is no longer on the form"});
        }
        return out;
    }, [shown, needle]);

    // ── one file ─────────────────────────────────────────────────────────────────────────────

    const renderFile = (attachment: Attachment, editable: boolean) => {
        // The conventional glyph for this kind of file, in the colour a desktop file manager would
        // use. A list of attachments is scanned rather than read -- "the PDF", "the spreadsheet" --
        // and a column of identical grey pages makes the reader check every name.
        const type = fileTypeFor(attachment.Name, attachment.MimeType);
        const TypeIcon = type.icon;
        return (
            <div key={attachment.LinkUSID}
                 style={{display: "flex", alignItems: "center", gap: 10, fontSize: 15,
                         color: Grey17}}>
                <Tooltip title={type.label}>
                    {/* THREE TIMES THE SIZE IT WAS (Ed, 2026-10-31: "Make the attachment icons 3x their
                        current size"): the file type is the first thing a reader scans a list of files
                        for, and at 24px it was a glyph beside the name rather than the mark of the row. */}
                    <TypeIcon style={{fontSize: 72, color: type.color, flex: "0 0 auto"}}/>
                </Tooltip>
                <span style={{flex: "1 1 auto", overflow: "hidden", textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              // STRUCK THROUGH, because the name is still the evidence of what WAS
                              // attached -- it is what somebody reconciles against a backup -- and
                              // it must not read as a file you can open.
                              textDecoration: attachment.Missing ? "line-through" : "none",
                              color: attachment.Missing ? Grey6 : Grey17}}>
                    {attachment.Name}
                </span>
                <span style={{color: Grey6, fontSize: 13, flex: "0 0 auto"}}>
                    {formatBytes(attachment.Bytes)}
                </span>
                {/* Provenance, not a status: an inherited file is as live as any other. Said plainly
                    so a draft's files do not read as though somebody attached them here. */}
                {/* THE CONTROLLED COPY, SAID ON THE ROW (2026-10-31). The rendition names itself; a released clean copy
                    still waiting for its rendition says so rather than looking like a released file with nothing
                    behind it; a draft's and an unreleased type's say nothing. */}
                {attachment.ControlledCopy === "rendition" ? (
                    <Tooltip title="Generated by the system at release: the file as attached, with the approval sheet appended. It cannot be removed.">
                        <span style={{color: "#1B5E20", fontSize: 12, flex: "0 0 auto", fontWeight: 600}}>controlled copy</span>
                    </Tooltip>
                ) : attachment.ControlledCopy === "pending" ? (
                    <Tooltip title="This revision is released; its controlled copy is being generated and appears under this file.">
                        <span style={{color: "#7A4B00", fontSize: 12, flex: "0 0 auto"}}>controlled copy pending</span>
                    </Tooltip>
                ) : null}
                {attachment.Origin === "INHERITED" ? (
                    <Tooltip title="Carried forward from the previous revision">
                        <span style={{color: Grey6, fontSize: 12, flex: "0 0 auto"}}>inherited</span>
                    </Tooltip>
                ) : null}
                {/* THE FILE IS GONE AND THE RECORD STILL LISTS IT. Shown rather than hidden: this
                    row is usually a File field's answer, so removing it from the panel would make
                    the question look unanswered with no account of where the answer went. No
                    download and no Ask -- both would fail, one of them confusingly. */}
                {attachment.Missing ? (
                    <Tooltip title="The stored file could not be found. It will need to be attached again.">
                        <span style={{color: Red3, fontSize: 12, flex: "0 0 auto",
                                      fontWeight: 600}}>
                            file missing
                        </span>
                    </Tooltip>
                ) : null}
                {/* ONE FILE. "What does this certificate say?" is a different question from "does
                    this record's evidence hold together", and the answer to the narrow one should
                    not be diluted by four other documents. */}
                {askable && !attachment.Missing ? (
                    <AskAboutAttachments
                        scope={{kind: "Attachment", attachmentUSID: attachment.USID}}
                        scopeLabel={attachment.Name}
                        tooltip={`Ask about ${attachment.Name}`}/>
                ) : null}
                {/* PREVIEW BESIDE DOWNLOAD (Ed, 2026-11-02), offered only for what a browser can draw: a button that
                    opens onto "cannot be previewed" is a no wearing a yes. */}
                {attachment.Missing || !previewKindFor(attachment.Name, attachment.MimeType) ? null : (
                    <Tooltip title="Preview">
                        <IconButton size="small" aria-label={`Preview ${attachment.Name}`} onClick={() => setPreviewing(attachment)}>
                            <VisibilityOutlined style={{fontSize: 18, color: Blue4}}/>
                        </IconButton>
                    </Tooltip>
                )}
                {attachment.Missing ? null : (
                    <Tooltip title="Download">
                        <IconButton size="small" aria-label={`Download ${attachment.Name}`} onClick={() => download(attachment)}>
                            <DownloadIcon style={{fontSize: 18, color: Blue4}}/>
                        </IconButton>
                    </Tooltip>
                )}
                {/* THE FILE'S OWN TRAIL, offered on a missing file too: who had it before it went is exactly
                    what somebody reconciling the loss needs. */}
                <Tooltip title="Audit trail">
                    <IconButton size="small" aria-label={`Audit trail of ${attachment.Name}`} onClick={() => setAuditing(attachment)}>
                        <HistoryIcon style={{fontSize: 18, color: Grey7}}/>
                    </IconButton>
                </Tooltip>
                {readOnly || !editable || attachment.ControlledCopy === "rendition" ? null : (
                    <IconButton size="small" onClick={() => remove(attachment)}>
                        <DeleteOutlineIcon style={{fontSize: 18, color: Grey7}}/>
                    </IconButton>
                )}
            </div>
        );
    };

    const ownFiles = attachments.filter(one => placementOf(one) === "Record" && !one.Missing).length;
    // EVERY FILE ON THE RECORD ITSELF, AT ONCE (Ed, 2026-10-31: "There also needs an ability download all
    // attachments associated to the object revision at once"): a button with the word on it, in the
    // section's own header, so it is seen before the list is read rather than found after it.
    const downloadAll = ownFiles > 0 ? (
        <Button size="small" variant="outlined" disabled={!!busy} onClick={bundle} startIcon={<DownloadIcon sx={{fontSize: 16}}/>}
                sx={{textTransform: "none", fontSize: 13, borderColor: Blue4, color: Blue4, flex: "0 0 auto"}}>
            {busy === "Bundling" ? "Bundling…" : `Download all ${ownFiles === 1 ? "(1 file, zip)" : `(${ownFiles} files, zip)`}`}
        </Button>
    ) : null;

    const addAFile = readOnly ? null : (
        <div style={{marginTop: 8}}>
            <input ref={input} type="file" style={{display: "none"}}
                   onChange={(e) => {
                       const file = e.target.files?.[0];
                       if (file) upload(file);
                   }}/>
            <button type="button"
                    disabled={!!busy || !sourceUSID}
                    onClick={() => input.current?.click()}
                    style={{fontSize: 13, color: Blue4, background: "none", border: "none",
                            padding: 0, cursor: busy ? "default" : "pointer"}}>
                {busy ? `${busy}…` : (single && attachments.length ? "Replace file" : "Add a file")}
            </button>
            {busy === "Uploading" ? (
                <LinearProgress variant="determinate" value={progress}
                                style={{marginTop: 6, maxWidth: 320}}/>
            ) : null}
        </div>
    );

    const dialogs = (
        <>
            {previewing ? (
                <AttachmentPreviewDialog name={previewing.Name} kind={previewKindFor(previewing.Name, previewing.MimeType)!}
                                         fetchUrl={() => previewUrl(previewing)}
                                         onDownload={() => download(previewing)}
                                         onClose={() => setPreviewing(null)}/>
            ) : null}
            {auditing ? (
                <AttachmentAuditTrailDialog attachmentUSID={auditing.USID} name={auditing.Name}
                                            onClose={() => setAuditing(null)}/>
            ) : null}
        </>
    );

    const errorLine = error ? (
        <Typography style={{fontSize: 12, color: Red3, marginTop: 6}}>{error}</Typography>
    ) : null;

    /**
     * A record with no files at all, which is the case this panel told four times over.
     *
     * ED, 2026-10-26, ON A SCREENSHOT OF IT: "This is very confusing." He was right, and the
     * interesting part is that every one of the four was added deliberately and is correct on its
     * own: the heading counts, the placement line explains why "On the record" can be empty while
     * the record has files, "No files." is the panel's empty state, and the record GROUP is drawn
     * even when empty because "Add a file" lives inside it. Each earns its place when there is
     * something to say. With nothing to say they became
     *
     *     0 files on this record / On the record · 0  On the form · 0 / No files. / — none —
     *
     * four claims, one fact, and a reader counting the ways is a reader wondering which of them is
     * about something else.
     *
     * SO THE EMPTY RECORD GETS ONE SENTENCE AND THE ACTION, and everything above stays exactly as
     * it was for every other case -- some files, all of them on the form, a filter hiding the
     * rest, a listing that failed. NOT an error, and not a filter: an error must never read as
     * "there are none" (the panel asserted both at once once already), and an empty FILTER result
     * is a different sentence that the filter line below already writes.
     */
    const nothingAtAll = !loading && !error && !needle && attachments.length === 0;

    // ── every file on the record, grouped ────────────────────────────────────────────────────

    if (allFiles) {
        // THREE THINGS, SAID AS THREE THINGS (Ed, 2026-10-31: "Give a clear indication that they have an
        // ability to ask about all the attachments, that there are attachments for the object revision
        // and there are attachments associated to the form"). The ask is a labelled button, not a glyph
        // a reader has to hover to understand; the record's own files and the form's files are two
        // boxed sections, each saying what belongs in it and where it is managed, with its own count.
        const recordGroup = groups.find(group => group.key === "record");
        const fieldGroups = groups.filter(group => group.key.startsWith("field:"));
        const documentGroup = groups.find(group => group.key === "document");
        const otherGroups = groups.filter(group => group.key !== "record" && !group.key.startsWith("field:") && group.key !== "document");
        const formCount = counts.field + counts.unclaimed;
        const section = (key: string, label: string, count: number, blurb: string, body: React.ReactNode, action: React.ReactNode = null) => (
            <div key={key} data-testid={`attachments-${key}`}
                 style={{border: `1px solid ${Grey2}`, borderRadius: 6, padding: "10px 12px 12px 12px"}}>
                <div style={{display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap"}}>
                    <Typography style={{fontSize: 12.5, fontWeight: 700, color: Grey17, textTransform: "uppercase", letterSpacing: 0.4}}>
                        {label}
                    </Typography>
                    <Typography style={{fontSize: 12.5, color: Grey6}}>{count} file{count === 1 ? "" : "s"}</Typography>
                    <div style={{flex: "1 1 auto"}}/>
                    {action}
                </div>
                <Typography style={{fontSize: 12, color: Grey6, marginTop: 2, marginBottom: 8, lineHeight: 1.5}}>{blurb}</Typography>
                {body}
            </div>
        );
        const none = (text: string) => <Typography style={{fontSize: 13, color: Grey6}}>{text}</Typography>;
        const rows = (files: Attachment[], editable: boolean) => (
            <div style={{display: "flex", flexDirection: "column", gap: 8}}>{files.map(one => renderFile(one, editable))}</div>
        );
        return (
            <div>
                <div style={{display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap"}}>
                    <Typography style={{fontSize: 14, fontWeight: 600, color: Grey17, flex: "0 0 auto"}}>
                        {loading || error
                            ? "Files on this record"
                            : nothingAtAll
                                ? "No files on this record"
                                : `${attachments.length} file${attachments.length === 1 ? "" : "s"} on this record`}
                    </Typography>
                    <div style={{flex: "1 1 auto"}}/>
                    {/* BY NAME, AND THE PLACEHOLDER SAYS SO: a box that said "Search" over a list of
                        files would be read as searching inside them, which the ask button does. */}
                    {attachments.length > 1 ? (
                        <TextField value={filter} size="small" placeholder="Filter by file name…"
                                   onChange={(e) => setFilter(e.target.value)}
                                   slotProps={{input: {startAdornment: (
                                       <InputAdornment position="start">
                                           <FilterListIcon style={{fontSize: 16, color: Grey6}}/>
                                       </InputAdornment>)}}}
                                   sx={{width: 230, "& .MuiInputBase-input": {fontSize: 13}}}/>
                    ) : null}
                    {/* ALL THE RECORD'S FILES, which is what somebody looking at a record means by
                        "the attachments" -- this panel's and every File field's together. The
                        service resolves the scope from the REVISION, so it reads exactly the files
                        listed below and nothing else. A BUTTON WITH WORDS ON IT: the sparkle alone
                        was a glyph a reader had to hover to understand. */}
                    {askable ? (
                        <AskAboutAttachments
                            scope={{kind: "Revision", objectRevisionUSID: sourceUSID}}
                            scopeLabel={`Every file on this record (${attachments.length})`}
                            tooltip="Ask a question answered from every file below, on the record and on the form">
                            {(open) => (
                                <Button size="small" variant="outlined" onClick={open} startIcon={<AutoAwesomeOutlined sx={{fontSize: 16}}/>}
                                        sx={{textTransform: "none", fontSize: 13, borderColor: Blue4, color: Blue4}}>
                                    Ask about all {attachments.length} file{attachments.length === 1 ? "" : "s"}
                                </Button>
                            )}
                        </AskAboutAttachments>
                    ) : null}
                </div>

                {loading ? <CircularProgress size={16} style={{marginTop: 10}}/> : (
                    <div style={{marginTop: 12, display: "flex", flexDirection: "column", gap: 12}}>
                        {needle && shown.length < attachments.length ? (
                            <Typography style={{fontSize: 11.5, color: Grey6}}>
                                {shown.length} of {attachments.length} shown — {attachments.length - shown.length}
                                {" "}hidden by the name filter.
                            </Typography>
                        ) : null}

                        {/* NAMED FOR THE RECORD (Ed, 2026-10-31: 'instead of saying "On the record" say
                            "Attachments associated to <Object ID> Rev n"'), so the section says whose files
                            these are rather than where in the layout they sit. */}
                        {section("record", recordLabel ? `Attachments associated to ${recordLabel}` : "Attachments on the record", counts.record,
                                 "Files attached to this revision itself: evidence, reports, certificates. Added and removed here; Download all takes every one of them as one zip.",
                                 <>
                                     {recordGroup && recordGroup.files.length > 0 ? rows(recordGroup.files, true) : (needle ? null : none("None yet."))}
                                     {addAFile}
                                 </>, downloadAll)}

                        {/* THE FORM'S FILES ARE A SECTION OF THEIR OWN, drawn whenever the record carries any
                            and left out on a record that has none, so an empty record is not told twice. */}
                        {formCount > 0 || fieldGroups.length > 0 || otherGroups.length > 0 ? section("form", "Attachments on the form", formCount,
                                 "Files answering the form's File fields, one group per field. They belong to the field: attach or remove them on the Form tab.",
                                 <div style={{display: "flex", flexDirection: "column", gap: 10}}>
                                     {fieldGroups.map(group => (
                                         <div key={group.key}>
                                             <Typography style={{fontSize: 12, fontWeight: 600, color: Grey7}}>{group.label}</Typography>
                                             <div style={{marginTop: 4}}>{rows(group.files, false)}</div>
                                         </div>
                                     ))}
                                     {otherGroups.map(group => (
                                         <div key={group.key}>
                                             <Typography style={{fontSize: 12, fontWeight: 600, color: Grey7}}>{group.label}</Typography>
                                             {group.note ? <Typography style={{fontSize: 11.5, color: Grey6, marginBottom: 4}}>{group.note}</Typography> : null}
                                             <div style={{marginTop: 4}}>{rows(group.files, false)}</div>
                                         </div>
                                     ))}
                                     {fieldGroups.length === 0 && otherGroups.length === 0 ? none("None.") : null}
                                 </div>) : null}

                        {documentGroup ? section("document", "In the document body", documentGroup.files.length,
                                 documentGroup.note ?? "Figures inside the document text, read in the document rather than here.",
                                 rows(documentGroup.files, false)) : null}
                    </div>
                )}

                {errorLine}
                {dialogs}
            </div>
        );
    }

    // ── one field's files, or a non-record source ────────────────────────────────────────────

    return (
        <div>
            {title ? (
                <div style={{display: "flex", alignItems: "center", gap: 4, marginBottom: 8}}>
                    <Typography style={{fontSize: 14, fontWeight: 600, color: Grey17}}>
                        {title}
                    </Typography>
                </div>
            ) : null}

            {loading ? (
                <CircularProgress size={16}/>
            ) : (
                <div>
                    {attachments.length === 0 ? (
                        <Typography style={{fontSize: 15, color: Grey6}}>
                            {error ? "" : "No files."}
                        </Typography>
                    ) : (
                        <div style={{display: "flex", flexDirection: "column", gap: 8}}>
                            {attachments.map(one => renderFile(one, true))}
                        </div>
                    )}

                    {addAFile}
                    {errorLine}
                </div>
            )}
            {dialogs}
        </div>
    );
}
